import React, { useCallback, useEffect, useState } from 'react'
import { ConfigProvider, DatePicker, Form, Radio, Row } from 'antd'
import Swal from 'sweetalert2'
import ContentHeader from '../../components/shared/ContentHeader'
import StatisticsGraph from '../../components/statistics/StatisticsGraph'
import StatisticsTable from '../../components/statistics/StatisticsTable'
import { useRecoilState } from 'recoil'
import { userState } from '../../store/user'
import dayjs from 'dayjs'
import statisticsApi from '../../api/statistics.api'
import CustomLoading from '../../components/shared/CustomLoading'
import styles from '../../styles/statistics.module.scss'
import { CustomDateButtonRadioTheme } from '../../styles/CustomAntdTheme'

const dateTypeItems = [
  { label: '1주일', value: '1week' },
  { label: '1개월', value: '1month' },
  { label: '3개월', value: '3month' },
]

const SalesStatisticsPage = () => {
  const [user, setUser] = useRecoilState(userState)
  const [isLoading, setIsLoading] = useState(false)

  const [navType, setNavType] = useState('mall')
  const [optionList, setOptionList] = useState([])

  const [dateType, setDateType] = useState('1week')
  const [searchOptions, setSearchOptions] = useState({
    searchType: 'order_created_at',
    searchStatus: 'gift_address_ready',
    searchDetailType: '',
    searchDetail: '',
    searchOrderId: '',
    funnel: 1, // 유입경로, 1:전체
    name: '',
    phone: '',
    startDate: dayjs().subtract(7, 'days').startOf('day'),
    endDate: dayjs().endOf('day'),
  })

  // Product-table
  const [sortOrder, setSortOrder] = useState({
    orderBy: 'desc', // desc | asc
    orderType: 'total_sales_amount', // total_sales_amount | total_purchases_num | unit_price_avg
  })

  const [tableLoading, setTableLoading] = useState(false)
  // const [selectedRowKey, setSelectedRowKey] = useState([]);
  // const [selectedRow, setSelectedRow] = useState([]);
  const [currentPageNumber, setCurrentPageNumber] = useState(1)
  const [totalSize, setTotalSize] = useState(0)
  // const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(10)

  const [summary, setSummary] = useState({})
  const [graphData, setGraphData] = useState({})
  const [productList, setProductList] = useState([])

  /**
   * 1주일, 1개월, 3개월
   * @param e
   */
  const handleDateTypeChange = (e) => {
    const value = e.target.value
    setDateType(value)
    switch (value) {
      case '1week':
        if (searchOptions.endDate === null || searchOptions.endDate === '') {
          setSearchOptions({
            ...searchOptions,
            startDate: dayjs().subtract(7, 'days').startOf('day'),
            endDate: dayjs().endOf('day'),
          })
        } else {
          setSearchOptions({
            ...searchOptions,
            startDate: dayjs(searchOptions.endDate.format('YYYY/MM/DD'))
              .subtract(7, 'days')
              .startOf('day'),
          })
        }
        break
      case '1month':
        if (searchOptions.endDate === null || searchOptions.endDate === '') {
          setSearchOptions({
            ...searchOptions,
            startDate: dayjs().subtract(1, 'months').startOf('day'),
            endDate: dayjs().endOf('day'),
          })
        } else {
          setSearchOptions({
            ...searchOptions,
            startDate: dayjs(searchOptions.endDate.format('YYYY/MM/DD'))
              .subtract(1, 'months')
              .startOf('day'),
          })
        }
        break
      case '3month':
        if (searchOptions.endDate === null || searchOptions.endDate === '') {
          setSearchOptions({
            ...searchOptions,
            startDate: dayjs().subtract(3, 'months').startOf('day'),
            endDate: dayjs().endOf('day'),
          })
        } else {
          setSearchOptions({
            ...searchOptions,
            startDate: dayjs(searchOptions.endDate.format('YYYY/MM/DD'))
              .subtract(3, 'months')
              .startOf('day'),
          })
        }
        break
    }
  }

  /**
   * 초기화
   */
  const handleInitial = () => {
    setSearchOptions({
      searchType: 'order_created_at',
      searchStatus: 'gift_address_ready',
      searchOrderId: '',
      funnel: 1,
      name: '',
      phone: '',
      startDate: dayjs().subtract(7, 'days').startOf('day'),
      endDate: dayjs().endOf('day'),
    })
    setDateType('1week')
    setCurrentPageNumber(1)
    setPageSize(10)
  }

  /**
   * 최대 30일 조회 기간 설정
   * @param value
   * @param type
   */
  const handleDatePicker = (value, type) => {
    setDateType('')
    if (type === 'startDate') {
      let diffDays = dayjs(searchOptions.endDate).diff(value, 'days')
      if (diffDays > 0) {
        if (diffDays >= 30) {
          // alert("조회 기간은 30일 이내로 설정 가능합니다.");
          Swal.fire({
            title: '조회 기간은 30일 이내로 설정 가능합니다.',
            customClass: 'custom-alert',
            confirmButtonColor: '#117ffa',
            confirmButtonText: '확인',
          })
          handleInitial()
        } else {
          setSearchOptions({ ...searchOptions, [type]: value })
        }
      } else {
        Swal.fire({
          title: '시작 날짜와 마지막 날짜를 정확히 선택해주세요.',
          customClass: 'custom-alert',
          confirmButtonColor: '#117ffa',
          confirmButtonText: '확인',
        })
        handleInitial()
      }
    } else {
      let diffDays = dayjs(value).diff(searchOptions.startDate, 'days')
      if (diffDays > 0) {
        if (diffDays >= 30) {
          Swal.fire({
            title: '조회 기간은 30일 이내로 설정 가능합니다.',
            customClass: 'custom-alert',
            confirmButtonColor: '#117ffa',
            confirmButtonText: '확인',
          })
          handleInitial()
        } else {
          setSearchOptions({ ...searchOptions, [type]: value })
        }
      } else {
        Swal.fire({
          title: '시작 날짜와 마지막 날짜를 정확히 선택해주세요.',
          customClass: 'custom-alert',
          confirmButtonColor: '#117ffa',
          confirmButtonText: '확인',
        })
        handleInitial()
      }
    }
  }

  useEffect(() => {
    getSalesDataGraph() // user, dateType, searchOptions, navType
  }, [user, dateType, searchOptions, navType])

  useEffect(() => {
    getSalesDataProduct() // user, searchOptions, navType, currentPageNumber, pageSize
  }, [
    user,
    dateType,
    searchOptions,
    sortOrder,
    navType,
    currentPageNumber,
    pageSize,
  ])

  /**
   * 매출 그래프 데이터 조회
   * @returns {Promise<void>}
   */
  const getSalesDataGraph = useCallback(async () => {
    setIsLoading(true)
    // 당영몰: 0, 드럭스토어 정보 가져올때 인플루언서 전체: 1, 나머지는 인플루언서 id
    let partners_id = navType === 'mall' ? 0 : searchOptions.funnel

    const param = {
      searchDateType: dateType?.length > 0 ? dateType : 'custom',
      startDate: dayjs(searchOptions.startDate)
        .startOf('day')
        .format('YYYY-MM-DD'),
      endDate: dayjs(searchOptions.endDate).endOf('day').format('YYYY-MM-DD'),
      partnersId: partners_id,
    }
    // const res = await statisticsApi.readSalesDataGraph(param, user.id)
    // if (res.code === 200) {
    //   setSummary(res.result.totalSummary)
    //   // total_sales_amount, total_purchases_num, unit_price_avg
    //   setGraphData(res.result.salesStatistics)
    // }
    setIsLoading(false)
  }, [user, dateType, searchOptions, navType])

  /**
   * 상품별 매출 내역
   * @type {(function(): Promise<void>)|*}
   */
  const getSalesDataProduct = useCallback(async () => {
    setTableLoading(true)
    // 당영몰: 0, 드럭스토어 정보 가져올때 인플루언서 전체: 1, 나머지는 인플루언서 id
    let partners_id = navType === 'mall' ? 0 : searchOptions.funnel

    const param = {
      offset: (currentPageNumber - 1) * pageSize,
      pageSize: pageSize,
      startDate: dayjs(searchOptions.startDate).format('YYYY-MM-DD'),
      endDate: dayjs(searchOptions.endDate).format('YYYY-MM-DD'),
      orderBy: sortOrder.orderBy,
      orderType: sortOrder.orderType, // total_sales_amount | total_purchases_num | unit_price_avg
      partnersId: partners_id,
    }

    // const res = await statisticsApi.readSalesDataProduct(param, user.id)
    // if (res.data.code === 200) {
    //   setTotalSize(res.data.result.totalCnt)
    //   setProductList(res.data.result.salesProductList ?? [])
    // }
    setTableLoading(false)
  }, [user, searchOptions, navType, sortOrder, currentPageNumber, pageSize])

  const handleChangeOffset = (pageNumber) => {
    setCurrentPageNumber(pageNumber)
  }

  return (
    <div style={{ position: 'relative' }}>
      {isLoading && <CustomLoading />}
      <ContentHeader title="판매분석" />
      <div className={`${styles['product-register-wrap']}`}>
        <div className={`${styles['product-search-box-wrap']}`}>
          <Form.Item
            label="조회기간"
            labelAlign="left"
            colon={false}
            labelCol={{ span: 3 }}
            wrapperCol={{ span: 21 }}
            style={{ marginBottom: 0 }}
          >
            <ConfigProvider theme={CustomDateButtonRadioTheme}>
              <Row align={'middle'} style={{ gap: '5px', rowGap: '5px' }}>
                <Radio.Group
                  className={`${styles['options-list']}`}
                  options={dateTypeItems}
                  onChange={handleDateTypeChange}
                  value={dateType}
                  optionType="button"
                />
                <DatePicker
                  className={`${styles['order-search-date-wrap']}`}
                  format="YYYY/MM/DD"
                  value={searchOptions.startDate}
                  onChange={(value) => handleDatePicker(value, 'startDate')}
                  allowClear={false}
                />
                ~
                <DatePicker
                  className={`${styles['order-search-date-wrap']}`}
                  format="YYYY/MM/DD"
                  value={searchOptions.endDate}
                  onChange={(value) => handleDatePicker(value, 'endDate')}
                  allowClear={false}
                />
              </Row>
            </ConfigProvider>
          </Form.Item>
        </div>
        <StatisticsGraph
          title={'판매분석'}
          searchOptions={searchOptions}
          summary={summary}
          graphData={graphData}
        />
        <StatisticsTable
          title={'상품별 상세내역'}
          data={{
            productList: productList,
            totalSize: totalSize,
            currentPageNumber: currentPageNumber,
            pageSize: pageSize,
            sortOrder: sortOrder,
          }}
          setSortOrder={setSortOrder}
          handleChangeOffset={handleChangeOffset}
          tableLoading={tableLoading}
        />
      </div>
    </div>
  )
}
export default SalesStatisticsPage
